/**
 * emailContentFromGmailSend — the single payload→email-fields mapping for
 * gmail_send proposal actions.
 *
 * Shared by every surface that renders or edits a proposed email (the
 * AI-actions review UI and the email composer), so the payload contract is
 * interpreted in exactly one place.
 */

import type { GmailSendPayload, ProposedAction } from './types';

/** The editable email fields extracted from a gmail_send action payload. */
export interface EmailContent {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body_html?: string;
  body: string;
  thread_id?: string;
  attachment_file_ids?: string[];
  // Reply fields - must be preserved for email threading
  is_reply?: boolean;
  reply_to_message_id?: string;
  // Multi-account OAuth support
  social_account_id?: number;
  // Per-email tracking opt-out
  disable_tracking?: boolean;
  // Signature state the backend stamped on the draft. Round-tripped verbatim:
  // the editor never derives it, but dropping it on save resets it to the
  // backend default ("none"), which (a) makes the send path append a SECOND
  // signature and (b) diverges the stored payload from the approve-time
  // snapshot → 409 STALE_ACTIONS (2026-08-18 incident).
  signature_disposition?: 'none' | 'inserted' | 'user_removed';
}

export function emailContentFromGmailSend(
  action: ProposedAction<GmailSendPayload>
): EmailContent {
  const params = action.payload.action_params;
  return {
    to: params.to,
    // Normalize empty cc/bcc to `undefined`. The backend defaults these to
    // `null` and echoes them back, but an empty-recipients draft sends them
    // as `undefined` (which JSON.stringify drops entirely). Without this
    // coercion, `null` (from the server) vs `undefined` (local) registers
    // as a content change on every save response — the editor resets and
    // remounts and re-saves forever (phantom edited→saving→saved loop).
    // This also matches what the header autosave emits for empty fields.
    cc: params.cc && params.cc.length > 0 ? params.cc : undefined,
    bcc: params.bcc && params.bcc.length > 0 ? params.bcc : undefined,
    subject: params.subject,
    body: params.body,
    body_html: params.body_html,
    thread_id: params.thread_id,
    attachment_file_ids: params.attachment_file_ids,
    // Preserve reply fields for email threading
    is_reply: params.is_reply,
    reply_to_message_id: params.reply_to_message_id,
    // Multi-account OAuth support
    social_account_id: params.social_account_id,
    // Per-email tracking opt-out
    disable_tracking: params.disable_tracking,
    // Backend-owned signature state — carried through so a save never resets it
    signature_disposition: params.signature_disposition,
  };
}
